/**
 * ARBT-1 — Arbitrage Bot section.
 *
 * Tabs: Overview · Exchanges · Strategies · Markets · Orders · Logs · API Keys
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Page } from '@/components/Page';
import { Section } from '@/components/Section';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { Spinner } from '@/components/Spinner';
import { Empty } from '@/components/Empty';
import { sb, useSupabaseQuery } from '@/hooks/useSupabaseQuery';
import { useAuth } from '@/providers/AuthProvider';
import { useHaptic } from '@/hooks/useHaptic';

// ── Types ────────────────────────────────────────────────────────────────────

type TabId = 'overview' | 'exchanges' | 'strategies' | 'markets' | 'orders' | 'logs' | 'apikeys';

interface Arbt1Config {
  is_enabled: boolean;
  dry_run: boolean;
  spot_enabled: boolean;
  futures_enabled: boolean;
  exec_mode: 'MAKER_ONLY' | 'SMART_LIMIT' | 'AGGRESSIVE_LIMIT';
  classic_arb_cfg: {
    enabled: boolean;
    budget_usd: number;
    max_open_positions: number;
    min_spread_pct: number;
    min_volume_usd: number;
    max_order_size_usd: number;
    max_daily_loss_usd: number;
  };
  funding_arb_cfg: {
    enabled: boolean;
    budget_usd: number;
    max_open_positions: number;
    min_funding_rate_pct: number;
    max_order_size_usd: number;
    max_daily_loss_usd: number;
  };
  risk_cfg: {
    max_slippage_pct: number;
    max_open_orders: number;
    max_retries: number;
    max_order_age_seconds: number;
    max_spread_allowed_pct: number;
    min_order_book_depth_usd: number;
    stale_ws_ms: number;
  };
  exchanges_enabled: Record<string, boolean>;
  kill_switch: boolean;
  updated_at: string;
}

interface Arbt1Market {
  id: number;
  exchange_id: string;
  market_type: 'spot' | 'futures';
  raw_symbol: string;
  symbol_key: string;
  base: string;
  quote: string;
  tick_size: number | null;
  lot_size: number | null;
  min_notional: number | null;
  updated_at: string;
}

interface Arbt1Order {
  id: number;
  strategy: string;
  exchange_id: string;
  symbol_key: string;
  market_type: string;
  side: string;
  order_type: string;
  exec_mode: string;
  price: number | null;
  qty: number;
  filled_qty: number;
  avg_fill_price: number | null;
  status: string;
  is_dry_run: boolean;
  created_at: string;
}

interface Arbt1Position {
  id: number;
  strategy: string;
  symbol_key: string;
  long_exchange: string;
  short_exchange: string;
  long_type: string;
  short_type: string;
  entry_spread: number | null;
  current_spread: number | null;
  unrealized_pnl: number | null;
  realized_pnl: number | null;
  status: string;
  is_dry_run: boolean;
  opened_at: string;
  closed_at: string | null;
}

interface Arbt1Decision {
  id: number;
  strategy: string | null;
  symbol_key: string | null;
  exchange_a: string | null;
  exchange_b: string | null;
  decision: string;
  reason: string | null;
  spread_pct: number | null;
  expected_pnl: number | null;
  actual_pnl: number | null;
  created_at: string;
}

interface Arbt1Metric {
  period_date: string;
  strategy: string;
  realized_pnl: number;
  total_fee: number;
  net_pnl: number;
}

interface ExchangeApiKeyRow {
  exchange: string;
  api_key: string | null;
  api_secret: string | null;
  passphrase: string | null;
  updated_at: string | null;
}

interface ExKey { apiKey: string; apiSecret: string; passphrase: string; updatedAt: string | null; }
type ExchKeysState = Record<string, ExKey>;

// ── Constants ────────────────────────────────────────────────────────────────

const ALL_EXCHANGES = [
  'binance', 'bybit', 'okx', 'gate', 'kucoin', 'bingx', 'blofin',
  'deribit', 'delta', 'weex', 'xt', 'kcex', 'mexc', 'bitget',
] as const;

type ArbtExchange = typeof ALL_EXCHANGES[number];

const EXCHANGE_LABELS: Record<ArbtExchange, string> = {
  binance: 'Binance', bybit: 'Bybit', okx: 'OKX', gate: 'Gate.io',
  kucoin: 'KuCoin', bingx: 'BingX', blofin: 'BloFin', deribit: 'Deribit',
  delta: 'Delta Exchange', weex: 'WEEX', xt: 'XT.com', kcex: 'KCEX', mexc: 'MEXC', bitget: 'Bitget',
};

const PASSPHRASE_EXCHANGES = new Set(['okx', 'kucoin', 'kcex']);

const TABS: { id: TabId; label: string }[] = [
  { id: 'overview',   label: 'Overview' },
  { id: 'exchanges',  label: 'Exchanges' },
  { id: 'strategies', label: 'Strategies' },
  { id: 'markets',    label: 'Markets' },
  { id: 'orders',     label: 'Orders' },
  { id: 'logs',       label: 'Logs' },
  { id: 'apikeys',    label: 'API Keys' },
];

const DEFAULT_CONFIG: Arbt1Config = {
  is_enabled: false,
  dry_run: true,
  spot_enabled: true,
  futures_enabled: true,
  exec_mode: 'MAKER_ONLY',
  classic_arb_cfg: { enabled: false, budget_usd: 1000, max_open_positions: 5, min_spread_pct: 0.1, min_volume_usd: 10000, max_order_size_usd: 500, max_daily_loss_usd: 200 },
  funding_arb_cfg: { enabled: false, budget_usd: 1000, max_open_positions: 3, min_funding_rate_pct: 0.01, max_order_size_usd: 500, max_daily_loss_usd: 200 },
  risk_cfg: { max_slippage_pct: 0.5, max_open_orders: 20, max_retries: 3, max_order_age_seconds: 60, max_spread_allowed_pct: 2.0, min_order_book_depth_usd: 5000, stale_ws_ms: 5000 },
  exchanges_enabled: {},
  kill_switch: false,
  updated_at: '',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDt(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru', { timeZone: 'UTC', hour12: false });
}

function fmtNum(v: number | null | undefined, dec = 4): string {
  if (v == null) return '—';
  return v.toFixed(dec);
}

function emptyKey(): ExKey { return { apiKey: '', apiSecret: '', passphrase: '', updatedAt: null }; }

function initExchKeys(): ExchKeysState {
  return Object.fromEntries(ALL_EXCHANGES.map((e) => [e, emptyKey()]));
}

// ── TabBar ───────────────────────────────────────────────────────────────────

function TabBar({ active, onChange }: { active: TabId; onChange: (t: TabId) => void }): JSX.Element {
  return (
    <div style={{ display: 'flex', overflowX: 'auto', margin: '0 var(--space-md) var(--space-md)', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--tg-separator)' }}>
      {TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          style={{ flex: '0 0 auto', minWidth: 72, minHeight: 36, border: 'none', background: active === t.id ? 'var(--tg-button)' : 'transparent', color: active === t.id ? 'var(--tg-button-text)' : 'var(--tg-text)', fontSize: 11, cursor: 'pointer', fontWeight: active === t.id ? 600 : 400, padding: '0 8px' }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────────────────

function OverviewTab({ userId }: { userId: string }): JSX.Element {
  const { notify } = useHaptic();
  const [acting, setActing] = useState(false);

  const configQ = useSupabaseQuery<Arbt1Config>(
    () => sb.from('arbt1_config').select('*').eq('user_id', userId).maybeSingle().then((r) => ({ data: r.data as Arbt1Config | null, error: r.error })),
    [userId],
    30_000,
  );

  const positionsQ = useSupabaseQuery<{ id: number }[]>(
    () => sb.from('arbt1_positions').select('id').eq('user_id', userId).eq('status', 'open'),
    [userId],
    30_000,
  );

  const today = new Date().toISOString().slice(0, 10);
  const metricsQ = useSupabaseQuery<Arbt1Metric[]>(
    () => sb.from('arbt1_metrics').select('period_date, strategy, realized_pnl, total_fee, net_pnl').eq('user_id', userId).eq('period_date', today),
    [userId],
    60_000,
  );

  const cfg = (configQ.data as unknown as Arbt1Config | null) ?? null;

  const totalNetPnl = (metricsQ.data ?? []).reduce((s, m) => s + (m.net_pnl ?? 0), 0);
  const openPositions = (positionsQ.data ?? []).length;

  async function activateKillSwitch(): Promise<void> {
    setActing(true);
    try {
      await Promise.all([
        sb.from('arbt1_config').upsert({ user_id: userId, kill_switch: true, updated_at: new Date().toISOString() }, { onConflict: 'user_id' }),
        sb.from('arbt1_jobs').insert({ user_id: userId, job_type: 'kill_switch', status: 'pending', created_at: new Date().toISOString(), updated_at: new Date().toISOString() }),
      ]);
      notify('success');
      configQ.refetch();
    } catch { notify('error'); }
    finally { setActing(false); }
  }

  async function cancelAll(): Promise<void> {
    setActing(true);
    try {
      await sb.from('arbt1_jobs').insert({ user_id: userId, job_type: 'cancel_all', status: 'pending', created_at: new Date().toISOString(), updated_at: new Date().toISOString() });
      notify('success');
    } catch { notify('error'); }
    finally { setActing(false); }
  }

  async function resetKillSwitch(): Promise<void> {
    setActing(true);
    try {
      const { error } = await sb.from('arbt1_config').upsert({ user_id: userId, kill_switch: false, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });
      if (error) throw error;
      notify('success');
      configQ.refetch();
    } catch { notify('error'); }
    finally { setActing(false); }
  }

  if (configQ.isLoading) return <Spinner />;

  const botStatus = cfg
    ? cfg.kill_switch ? '🔴 Kill Switch Active'
      : !cfg.is_enabled ? '⬛ Stopped'
      : cfg.dry_run ? '🟡 Dry Run'
      : '🟢 Live'
    : '⬛ Not configured';

  return (
    <>
      <Section header="Bot Status">
        <div style={{ padding: 'var(--space-md) var(--space-lg)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: 'var(--tg-hint)', fontSize: 13 }}>Status</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{botStatus}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: 'var(--tg-hint)', fontSize: 13 }}>Open Positions</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{openPositions}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <span style={{ color: 'var(--tg-hint)', fontSize: 13 }}>Today Net PnL</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: totalNetPnl >= 0 ? '#34c759' : 'var(--tg-destructive)' }}>
              {totalNetPnl >= 0 ? '+' : ''}{fmtNum(totalNetPnl, 2)} USD
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" disabled={acting} onClick={() => void cancelAll()} style={{ flex: 1 }}>
              Cancel All
            </Button>
            {cfg?.kill_switch ? (
              <Button
                variant="primary"
                disabled={acting}
                onClick={() => void resetKillSwitch()}
                style={{ flex: 1 }}
              >
                Снять Kill Switch
              </Button>
            ) : (
              <Button
                variant="primary"
                disabled={acting}
                onClick={() => void activateKillSwitch()}
                style={{ flex: 1, background: 'var(--tg-destructive)', color: '#fff' }}
              >
                🛑 Kill Switch
              </Button>
            )}
          </div>
        </div>
      </Section>
    </>
  );
}

// ── Exchanges Tab ──────────────────────────────────────────────────────────────

function ExchangesTab({ userId }: { userId: string }): JSX.Element {
  const { notify } = useHaptic();
  const [saving, setSaving] = useState(false);

  const configQ = useSupabaseQuery<Pick<Arbt1Config, 'exchanges_enabled'>>(
    () => sb.from('arbt1_config').select('exchanges_enabled').eq('user_id', userId).maybeSingle().then((r) => ({ data: r.data as Pick<Arbt1Config, 'exchanges_enabled'> | null, error: r.error })),
    [userId],
    60_000,
  );

  const cfg = configQ.data ?? null;
  const enabled: Record<string, boolean> = (cfg?.exchanges_enabled as Record<string, boolean> | undefined) ?? {};

  async function toggleExchange(id: string, value: boolean): Promise<void> {
    setSaving(true);
    try {
      const next = { ...enabled, [id]: value };
      await sb.from('arbt1_config').upsert(
        { user_id: userId, exchanges_enabled: next, updated_at: new Date().toISOString() },
        { onConflict: 'user_id' },
      );
      notify('success');
      configQ.refetch();
    } catch { notify('error'); }
    finally { setSaving(false); }
  }

  if (configQ.isLoading) return <Spinner />;

  return (
    <Section header="Enabled Exchanges" footer="Enable exchanges to include them in market scanning and arbitrage.">
      {ALL_EXCHANGES.map((id) => {
        const isEnabled = enabled[id] ?? false;
        return (
          <div key={id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-sm) var(--space-lg)', borderBottom: '0.5px solid var(--tg-separator)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: isEnabled ? '#34c759' : 'var(--tg-hint)', display: 'inline-block' }} />
              <span style={{ fontSize: 14 }}>{EXCHANGE_LABELS[id]}</span>
            </div>
            <label style={{ display: 'inline-flex', alignItems: 'center', cursor: saving ? 'wait' : 'pointer' }}>
              <input
                type="checkbox"
                checked={isEnabled}
                disabled={saving}
                onChange={(e) => void toggleExchange(id, e.target.checked)}
                style={{ width: 36, height: 20, cursor: saving ? 'wait' : 'pointer' }}
              />
            </label>
          </div>
        );
      })}
    </Section>
  );
}

// ── Strategies Tab ────────────────────────────────────────────────────────────

function StrategiesTab({ userId }: { userId: string }): JSX.Element {
  const { notify } = useHaptic();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [cfg, setCfg] = useState<Arbt1Config>(DEFAULT_CONFIG);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    void sb.from('arbt1_config').select('*').eq('user_id', userId).maybeSingle().then(({ data }) => {
      if (data) setCfg({ ...DEFAULT_CONFIG, ...(data as unknown as Partial<Arbt1Config>) });
      setLoaded(true);
    });
  }, [userId]);

  const saveDebounced = useCallback((newCfg: Arbt1Config): void => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void sb.from('arbt1_config').upsert(
        { user_id: userId, ...newCfg, updated_at: new Date().toISOString() },
        { onConflict: 'user_id' },
      ).then(({ error }) => {
        if (error) { console.error('[Arbt1][Strategies] save error:', error.message); notify('error'); }
        else notify('success');
      });
    }, 600);
  }, [userId, notify]);

  function update(patch: Partial<Arbt1Config>): void {
    const next = { ...cfg, ...patch };
    setCfg(next);
    saveDebounced(next);
  }

  function updateClassic(patch: Partial<Arbt1Config['classic_arb_cfg']>): void {
    update({ classic_arb_cfg: { ...cfg.classic_arb_cfg, ...patch } });
  }

  function updateFunding(patch: Partial<Arbt1Config['funding_arb_cfg']>): void {
    update({ funding_arb_cfg: { ...cfg.funding_arb_cfg, ...patch } });
  }

  function updateRisk(patch: Partial<Arbt1Config['risk_cfg']>): void {
    update({ risk_cfg: { ...cfg.risk_cfg, ...patch } });
  }

  if (!loaded) return <Spinner />;

  return (
    <>
      <Section header="Global Settings">
        <label style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--space-sm) var(--space-lg)', borderBottom: '0.5px solid var(--tg-separator)' }}>
          <span>Bot Enabled</span>
          <input type="checkbox" checked={cfg.is_enabled} onChange={(e) => update({ is_enabled: e.target.checked })} />
        </label>
        <label style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--space-sm) var(--space-lg)', borderBottom: '0.5px solid var(--tg-separator)' }}>
          <span>Dry Run (no real orders)</span>
          <input type="checkbox" checked={cfg.dry_run} onChange={(e) => update({ dry_run: e.target.checked })} />
        </label>
        {!cfg.dry_run && (
          <div style={{ padding: 'var(--space-sm) var(--space-lg)', fontSize: 12, color: 'var(--tg-destructive)', borderBottom: '0.5px solid var(--tg-separator)' }}>
            Live-торговля в ARBT-1 пока не реализована (адаптеры бирж только читают рынок). С выключенным Dry Run бот не открывает сделки — причина пишется в Logs.
          </div>
        )}
        <label style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--space-sm) var(--space-lg)', borderBottom: '0.5px solid var(--tg-separator)' }}>
          <span>Spot Markets</span>
          <input type="checkbox" checked={cfg.spot_enabled} onChange={(e) => update({ spot_enabled: e.target.checked })} />
        </label>
        <label style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--space-sm) var(--space-lg)', borderBottom: '0.5px solid var(--tg-separator)' }}>
          <span>Futures Markets</span>
          <input type="checkbox" checked={cfg.futures_enabled} onChange={(e) => update({ futures_enabled: e.target.checked })} />
        </label>
        <div style={{ padding: 'var(--space-sm) var(--space-lg)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13 }}>Execution Mode</span>
          <select
            value={cfg.exec_mode}
            onChange={(e) => update({ exec_mode: e.target.value as Arbt1Config['exec_mode'] })}
            style={{ fontSize: 13, padding: '4px 8px', borderRadius: 6, border: '0.5px solid var(--tg-separator)', background: 'var(--tg-bg)', color: 'var(--tg-text)' }}
          >
            <option value="MAKER_ONLY">MAKER_ONLY</option>
            <option value="SMART_LIMIT">SMART_LIMIT</option>
            <option value="AGGRESSIVE_LIMIT">AGGRESSIVE_LIMIT</option>
          </select>
        </div>
      </Section>

      <Section header="Classic Arbitrage">
        <label style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--space-sm) var(--space-lg)', borderBottom: '0.5px solid var(--tg-separator)' }}>
          <span>Enabled</span>
          <input type="checkbox" checked={cfg.classic_arb_cfg.enabled} onChange={(e) => updateClassic({ enabled: e.target.checked })} />
        </label>
        <Input label="Budget (USD)" value={String(cfg.classic_arb_cfg.budget_usd)} onChange={(e) => updateClassic({ budget_usd: Number(e.target.value) })} />
        <Input label="Min Spread (%)" value={String(cfg.classic_arb_cfg.min_spread_pct)} onChange={(e) => updateClassic({ min_spread_pct: Number(e.target.value) })} />
        <Input label="Min Volume (USD)" value={String(cfg.classic_arb_cfg.min_volume_usd)} onChange={(e) => updateClassic({ min_volume_usd: Number(e.target.value) })} />
        <Input label="Max Order Size (USD)" value={String(cfg.classic_arb_cfg.max_order_size_usd)} onChange={(e) => updateClassic({ max_order_size_usd: Number(e.target.value) })} />
        <Input label="Max Daily Loss (USD)" value={String(cfg.classic_arb_cfg.max_daily_loss_usd)} onChange={(e) => updateClassic({ max_daily_loss_usd: Number(e.target.value) })} />
        <Input label="Max Open Positions" value={String(cfg.classic_arb_cfg.max_open_positions)} onChange={(e) => updateClassic({ max_open_positions: Number(e.target.value) })} />
      </Section>

      <Section header="Funding Rate Arbitrage">
        <label style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--space-sm) var(--space-lg)', borderBottom: '0.5px solid var(--tg-separator)' }}>
          <span>Enabled</span>
          <input type="checkbox" checked={cfg.funding_arb_cfg.enabled} onChange={(e) => updateFunding({ enabled: e.target.checked })} />
        </label>
        <Input label="Budget (USD)" value={String(cfg.funding_arb_cfg.budget_usd)} onChange={(e) => updateFunding({ budget_usd: Number(e.target.value) })} />
        <Input label="Min Funding Rate (%)" value={String(cfg.funding_arb_cfg.min_funding_rate_pct)} onChange={(e) => updateFunding({ min_funding_rate_pct: Number(e.target.value) })} />
        <Input label="Max Order Size (USD)" value={String(cfg.funding_arb_cfg.max_order_size_usd)} onChange={(e) => updateFunding({ max_order_size_usd: Number(e.target.value) })} />
        <Input label="Max Daily Loss (USD)" value={String(cfg.funding_arb_cfg.max_daily_loss_usd)} onChange={(e) => updateFunding({ max_daily_loss_usd: Number(e.target.value) })} />
        <Input label="Max Open Positions" value={String(cfg.funding_arb_cfg.max_open_positions)} onChange={(e) => updateFunding({ max_open_positions: Number(e.target.value) })} />
      </Section>

      <Section header="Risk Limits">
        <Input label="Max Slippage (%)" value={String(cfg.risk_cfg.max_slippage_pct)} onChange={(e) => updateRisk({ max_slippage_pct: Number(e.target.value) })} />
        <Input label="Max Open Orders" value={String(cfg.risk_cfg.max_open_orders)} onChange={(e) => updateRisk({ max_open_orders: Number(e.target.value) })} />
        <Input label="Max Retries" value={String(cfg.risk_cfg.max_retries)} onChange={(e) => updateRisk({ max_retries: Number(e.target.value) })} />
        <Input label="Max Order Age (s)" value={String(cfg.risk_cfg.max_order_age_seconds)} onChange={(e) => updateRisk({ max_order_age_seconds: Number(e.target.value) })} />
        <Input label="Max Spread Allowed (%)" value={String(cfg.risk_cfg.max_spread_allowed_pct)} onChange={(e) => updateRisk({ max_spread_allowed_pct: Number(e.target.value) })} />
        <Input label="Min OB Depth (USD)" value={String(cfg.risk_cfg.min_order_book_depth_usd)} onChange={(e) => updateRisk({ min_order_book_depth_usd: Number(e.target.value) })} />
        <Input label="Stale WS (ms)" value={String(cfg.risk_cfg.stale_ws_ms)} onChange={(e) => updateRisk({ stale_ws_ms: Number(e.target.value) })} />
      </Section>
    </>
  );
}

// ── Markets Tab ───────────────────────────────────────────────────────────────

function MarketsTab({ userId }: { userId: string }): JSX.Element {
  const { notify } = useHaptic();
  const [filterExchange, setFilterExchange] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'spot' | 'futures'>('all');
  const [filterSymbol, setFilterSymbol] = useState('');
  const [scanning, setScanning] = useState(false);

  const marketsQ = useSupabaseQuery<Arbt1Market[]>(
    () => sb.from('arbt1_markets').select('*').eq('user_id', userId).order('updated_at', { ascending: false }).limit(500),
    [userId],
    60_000,
  );

  const rows = (marketsQ.data ?? []).filter((m) => {
    if (filterExchange && m.exchange_id !== filterExchange) return false;
    if (filterType !== 'all' && m.market_type !== filterType) return false;
    if (filterSymbol && !m.symbol_key.toLowerCase().includes(filterSymbol.toLowerCase())) return false;
    return true;
  });

  async function scanNow(): Promise<void> {
    setScanning(true);
    try {
      await sb.from('arbt1_jobs').insert({ user_id: userId, job_type: 'scan_markets', status: 'pending', created_at: new Date().toISOString(), updated_at: new Date().toISOString() });
      notify('success');
    } catch { notify('error'); }
    finally { setScanning(false); }
  }

  if (marketsQ.isLoading) return <Spinner />;

  return (
    <>
      <Section header="Filters">
        <div style={{ padding: 'var(--space-sm) var(--space-lg)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select
            value={filterExchange}
            onChange={(e) => setFilterExchange(e.target.value)}
            style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, border: '0.5px solid var(--tg-separator)', background: 'var(--tg-bg)', color: 'var(--tg-text)' }}
          >
            <option value="">All Exchanges</option>
            {ALL_EXCHANGES.map((e) => <option key={e} value={e}>{EXCHANGE_LABELS[e]}</option>)}
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as typeof filterType)}
            style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, border: '0.5px solid var(--tg-separator)', background: 'var(--tg-bg)', color: 'var(--tg-text)' }}
          >
            <option value="all">All Types</option>
            <option value="spot">Spot</option>
            <option value="futures">Futures</option>
          </select>
          <input
            placeholder="Search symbol…"
            value={filterSymbol}
            onChange={(e) => setFilterSymbol(e.target.value)}
            style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, border: '0.5px solid var(--tg-separator)', background: 'var(--tg-bg)', color: 'var(--tg-text)', flex: 1, minWidth: 100 }}
          />
        </div>
        <div style={{ padding: '0 var(--space-lg) var(--space-sm)' }}>
          <Button variant="secondary" disabled={scanning} onClick={() => void scanNow()}>
            {scanning ? '…' : '🔄 Refresh Now'}
          </Button>
        </div>
      </Section>

      {rows.length === 0 ? (
        <Empty icon="📊" title="No markets" hint="Click Refresh Now to scan markets, or enable exchanges first." />
      ) : (
        <Section header={`Markets (${rows.length})`}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ fontSize: 11, borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr style={{ color: 'var(--tg-hint)', borderBottom: '0.5px solid var(--tg-separator)' }}>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Exchange</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Symbol</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px' }}>Tick</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px' }}>Lot</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={m.id} style={{ borderBottom: '0.5px solid var(--tg-separator)' }}>
                    <td style={{ padding: '4px 8px' }}>{m.exchange_id}</td>
                    <td style={{ padding: '4px 8px' }}>{m.market_type}</td>
                    <td style={{ padding: '4px 8px', fontWeight: 600 }}>{m.symbol_key}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>{m.tick_size ?? '—'}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>{m.lot_size ?? '—'}</td>
                    <td style={{ padding: '4px 8px', color: 'var(--tg-hint)' }}>{fmtDt(m.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </>
  );
}

// ── Orders Tab ────────────────────────────────────────────────────────────────

function OrdersTab({ userId }: { userId: string }): JSX.Element {
  const { notify } = useHaptic();
  const [cancelling, setCancelling] = useState(false);

  const positionsQ = useSupabaseQuery<Arbt1Position[]>(
    () => sb.from('arbt1_positions').select('*').eq('user_id', userId).order('opened_at', { ascending: false }).limit(100),
    [userId],
    30_000,
  );

  const ordersQ = useSupabaseQuery<Arbt1Order[]>(
    () => sb.from('arbt1_orders').select('*').eq('user_id', userId).order('created_at', { ascending: false }).limit(200),
    [userId],
    30_000,
  );

  async function cancelAll(): Promise<void> {
    setCancelling(true);
    try {
      await sb.from('arbt1_jobs').insert({ user_id: userId, job_type: 'cancel_all', status: 'pending', created_at: new Date().toISOString(), updated_at: new Date().toISOString() });
      notify('success');
    } catch { notify('error'); }
    finally { setCancelling(false); }
  }

  return (
    <>
      <div style={{ padding: 'var(--space-sm) var(--space-md)' }}>
        <Button variant="secondary" disabled={cancelling} onClick={() => void cancelAll()}>
          {cancelling ? '…' : 'Cancel All Orders'}
        </Button>
      </div>

      <Section header={`Positions (open: ${(positionsQ.data ?? []).filter((p) => p.status === 'open').length})`}>
        {positionsQ.isLoading ? <Spinner /> : (positionsQ.data ?? []).length === 0 ? (
          <Empty icon="📈" title="No positions" hint="No arbitrage positions yet." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ fontSize: 11, borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr style={{ color: 'var(--tg-hint)', borderBottom: '0.5px solid var(--tg-separator)' }}>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Strategy</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Symbol</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Long</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Short</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px' }}>Spread</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px' }}>PnL</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {(positionsQ.data ?? []).map((p) => (
                  <tr key={p.id} style={{ borderBottom: '0.5px solid var(--tg-separator)' }}>
                    <td style={{ padding: '4px 8px' }}>{p.strategy}</td>
                    <td style={{ padding: '4px 8px', fontWeight: 600 }}>{p.symbol_key}</td>
                    <td style={{ padding: '4px 8px' }}>{p.long_exchange}</td>
                    <td style={{ padding: '4px 8px' }}>{p.short_exchange}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>{fmtNum(p.current_spread)}%</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', color: ((p.status === 'open' ? p.unrealized_pnl : p.realized_pnl) ?? 0) >= 0 ? '#34c759' : 'var(--tg-destructive)' }}>{fmtNum(p.status === 'open' ? p.unrealized_pnl : p.realized_pnl, 2)}</td>
                    <td style={{ padding: '4px 8px' }}>{p.is_dry_run ? '🏖️ ' : ''}{p.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section header={`Orders (${(ordersQ.data ?? []).length})`}>
        {ordersQ.isLoading ? <Spinner /> : (ordersQ.data ?? []).length === 0 ? (
          <Empty icon="📋" title="No orders" hint="No orders recorded yet." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ fontSize: 11, borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr style={{ color: 'var(--tg-hint)', borderBottom: '0.5px solid var(--tg-separator)' }}>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Exchange</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Symbol</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Side</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px' }}>Qty</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px' }}>Filled</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Time</th>
                </tr>
              </thead>
              <tbody>
                {(ordersQ.data ?? []).map((o) => (
                  <tr key={o.id} style={{ borderBottom: '0.5px solid var(--tg-separator)' }}>
                    <td style={{ padding: '4px 8px' }}>{o.exchange_id}</td>
                    <td style={{ padding: '4px 8px', fontWeight: 600 }}>{o.symbol_key}</td>
                    <td style={{ padding: '4px 8px', color: o.side === 'buy' ? '#34c759' : 'var(--tg-destructive)' }}>{o.side}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>{fmtNum(o.qty, 4)}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>{fmtNum(o.filled_qty, 4)}</td>
                    <td style={{ padding: '4px 8px' }}>{o.is_dry_run ? '🏖️ ' : ''}{o.status}</td>
                    <td style={{ padding: '4px 8px', color: 'var(--tg-hint)' }}>{fmtDt(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </>
  );
}

// ── Logs Tab ──────────────────────────────────────────────────────────────────

function LogsTab({ userId }: { userId: string }): JSX.Element {
  const [filterStrategy, setFilterStrategy] = useState('');
  const [filterDecision, setFilterDecision] = useState('');
  const [filterSymbol, setFilterSymbol] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const decisionsQ = useSupabaseQuery<Arbt1Decision[]>(
    () => sb.from('arbt1_decisions').select('id, strategy, symbol_key, exchange_a, exchange_b, decision, reason, spread_pct, expected_pnl, actual_pnl, created_at').eq('user_id', userId).order('created_at', { ascending: false }).limit(200),
    [userId],
    30_000,
  );

  const rows = (decisionsQ.data ?? []).filter((d) => {
    if (filterStrategy && d.strategy !== filterStrategy) return false;
    if (filterDecision && d.decision !== filterDecision) return false;
    if (filterSymbol && !((d.symbol_key ?? '').toLowerCase().includes(filterSymbol.toLowerCase()))) return false;
    return true;
  });

  if (decisionsQ.isLoading) return <Spinner />;

  return (
    <>
      <Section header="Filters">
        <div style={{ padding: 'var(--space-sm) var(--space-lg)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select
            value={filterStrategy}
            onChange={(e) => setFilterStrategy(e.target.value)}
            style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, border: '0.5px solid var(--tg-separator)', background: 'var(--tg-bg)', color: 'var(--tg-text)' }}
          >
            <option value="">All Strategies</option>
            <option value="classic_arb">Classic Arb</option>
            <option value="funding_arb">Funding Arb</option>
          </select>
          <select
            value={filterDecision}
            onChange={(e) => setFilterDecision(e.target.value)}
            style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, border: '0.5px solid var(--tg-separator)', background: 'var(--tg-bg)', color: 'var(--tg-text)' }}
          >
            <option value="">All Decisions</option>
            <option value="open">open</option>
            <option value="close">close</option>
            <option value="skip">skip</option>
            <option value="kill_switch">kill_switch</option>
            <option value="scan">scan</option>
          </select>
          <input
            placeholder="Search symbol…"
            value={filterSymbol}
            onChange={(e) => setFilterSymbol(e.target.value)}
            style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, border: '0.5px solid var(--tg-separator)', background: 'var(--tg-bg)', color: 'var(--tg-text)', flex: 1, minWidth: 100 }}
          />
        </div>
      </Section>

      {rows.length === 0 ? (
        <Empty icon="📝" title="No decisions" hint="Decision log will appear here as the bot runs." />
      ) : (
        <Section header={`Decisions (${rows.length})`}>
          {rows.map((d) => (
            <div key={d.id} style={{ padding: 'var(--space-sm) var(--space-lg)', borderBottom: '0.5px solid var(--tg-separator)' }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2, cursor: 'pointer' }}
                onClick={() => setExpandedId(expandedId === d.id ? null : d.id)}
              >
                <span style={{ fontWeight: 600, fontSize: 12 }}>
                  <span style={{ color: d.decision === 'open' ? '#34c759' : d.decision === 'kill_switch' ? 'var(--tg-destructive)' : 'var(--tg-text)' }}>
                    {d.decision}
                  </span>
                  {d.symbol_key && ` · ${d.symbol_key}`}
                </span>
                <span style={{ fontSize: 11, color: 'var(--tg-hint)' }}>{fmtDt(d.created_at)}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--tg-hint)' }}>
                {d.strategy ?? ''}{d.exchange_a ? ` · ${d.exchange_a}↔${d.exchange_b ?? ''}` : ''}
                {d.spread_pct != null ? ` · spread ${fmtNum(d.spread_pct, 4)}%` : ''}
                {d.expected_pnl != null ? ` · exp ${fmtNum(d.expected_pnl, 4)}` : ''}
              </div>
              {d.reason && <div style={{ fontSize: 11, color: 'var(--tg-hint)', marginTop: 2 }}>{d.reason}</div>}
              {expandedId === d.id && d.actual_pnl != null && (
                <div style={{ fontSize: 11, color: '#34c759', marginTop: 2 }}>actual PnL: {fmtNum(d.actual_pnl, 4)}</div>
              )}
            </div>
          ))}
        </Section>
      )}
    </>
  );
}

// ── API Keys Tab ──────────────────────────────────────────────────────────────

function ApiKeysTab({ userId }: { userId: string }): JSX.Element {
  const { notify } = useHaptic();
  const [exchKeys, setExchKeys] = useState<ExchKeysState>(initExchKeys);
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  useEffect(() => {
    void sb.from('exchange_api_keys').select('exchange, api_key, api_secret, passphrase, updated_at').then(({ data, error }) => {
      if (error) { console.error('[Arbt1][ApiKeys] load error:', error.message); return; }
      if (data) {
        setExchKeys((prev) => {
          const next = { ...prev };
          for (const row of data as ExchangeApiKeyRow[]) {
            if (row.exchange in next) {
              next[row.exchange] = { apiKey: row.api_key ?? '', apiSecret: row.api_secret ?? '', passphrase: row.passphrase ?? '', updatedAt: row.updated_at };
            }
          }
          return next;
        });
      }
    });
  }, [userId]);

  function updateKey(id: string, field: keyof Omit<ExKey, 'updatedAt'>, value: string): void {
    setExchKeys((prev) => ({ ...prev, [id]: { ...(prev[id] ?? emptyKey()), [field]: value } }));
  }

  async function saveKey(id: string): Promise<void> {
    const k = exchKeys[id] ?? emptyKey();
    setSaving((prev) => ({ ...prev, [id]: true }));
    const { error } = await sb.from('exchange_api_keys').upsert(
      { user_id: userId, exchange: id, api_key: k.apiKey || null, api_secret: k.apiSecret || null, passphrase: k.passphrase || null, updated_at: new Date().toISOString() },
      { onConflict: 'user_id,exchange' },
    );
    setSaving((prev) => ({ ...prev, [id]: false }));
    if (error) { console.error(`[Arbt1][ApiKeys] save ${id}:`, error.message); notify('error'); }
    else notify('success');
  }

  return (
    <>
      {ALL_EXCHANGES.map((id) => {
        const label = EXCHANGE_LABELS[id];
        const k = exchKeys[id] ?? emptyKey();
        const isSaving = saving[id] ?? false;
        const needsPassphrase = PASSPHRASE_EXCHANGES.has(id);
        return (
          <Section
            key={id}
            header={`${label} API`}
            footer={k.updatedAt ? `Last updated: ${fmtDt(k.updatedAt)}` : 'Keys are protected by RLS — only you can access them.'}
          >
            <Input label="API Key" value={k.apiKey} onChange={(e) => updateKey(id, 'apiKey', e.target.value)} onBlur={() => void saveKey(id)} />
            <Input label="API Secret" type="password" value={k.apiSecret} onChange={(e) => updateKey(id, 'apiSecret', e.target.value)} onBlur={() => void saveKey(id)} />
            {needsPassphrase && (
              <Input label="Passphrase" type="password" value={k.passphrase} onChange={(e) => updateKey(id, 'passphrase', e.target.value)} onBlur={() => void saveKey(id)} />
            )}
            <div style={{ padding: 'var(--space-sm) var(--space-lg)' }}>
              <Button variant="primary" disabled={isSaving} onClick={() => void saveKey(id)}>
                {isSaving ? '…' : 'Save'}
              </Button>
            </div>
          </Section>
        );
      })}
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

/** ARBT-1 — Arbitrage Bot (13 exchanges). */
export function Arbt1Page(): JSX.Element {
  const { userId } = useAuth();
  const [tab, setTab] = useState<TabId>('overview');

  if (!userId) {
    return (
      <Page title="ARBT-1">
        <Empty icon="🔐" title="Не авторизован" hint="Откройте приложение через Telegram." />
      </Page>
    );
  }

  return (
    <Page title="ARBT-1">
      <TabBar active={tab} onChange={setTab} />
      {tab === 'overview'   && <OverviewTab userId={userId} />}
      {tab === 'exchanges'  && <ExchangesTab userId={userId} />}
      {tab === 'strategies' && <StrategiesTab userId={userId} />}
      {tab === 'markets'    && <MarketsTab userId={userId} />}
      {tab === 'orders'     && <OrdersTab userId={userId} />}
      {tab === 'logs'       && <LogsTab userId={userId} />}
      {tab === 'apikeys'    && <ApiKeysTab userId={userId} />}
    </Page>
  );
}
